import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from 'src/components/Input';
import { DraggableCrmField, CrmField } from '../DraggableCrmField';
import { ContactRecord } from 'src/services/integration-app.service';
import { useFieldMappingDnd } from './FieldMappingDndContext';

interface SourceColumnProps {
  filteredCrmFields: CrmField[];
  sampleContacts: ContactRecord[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export const SourceColumn: React.FC<SourceColumnProps> = ({
  filteredCrmFields,
  sampleContacts,
  searchQuery,
  onSearchChange,
}) => {
  const { setNodeRef } = useDroppable({
    id: 'source',
  });

  const { activeFieldId, activeSourceContainer, overContainerId, insertIndex } =
    useFieldMappingDnd();

  // Show placeholder when dragging from a target container to source
  const showPlaceholder =
    activeFieldId !== null &&
    activeSourceContainer !== 'source' &&
    overContainerId === 'source';

  // Placeholder component styled like a blank field (matching example app)
  const Placeholder = (
    <div className="h-[52px] bg-neutral-200/50 border border-dashed border-primary/30 rounded-lg opacity-50" />
  );

  // Build the list with placeholder at the correct position
  const renderFieldsWithPlaceholder = () => {
    if (!showPlaceholder) {
      return filteredCrmFields.map((field) => (
        <DraggableCrmField
          key={field.value}
          field={field}
          sampleContacts={sampleContacts}
        />
      ));
    }

    const effectiveInsertIndex = Math.max(
      0,
      Math.min(insertIndex, filteredCrmFields.length),
    );

    const items = filteredCrmFields.map((field) => (
      <DraggableCrmField
        key={field.value}
        field={field}
        sampleContacts={sampleContacts}
      />
    ));

    items.splice(
      effectiveInsertIndex,
      0,
      <React.Fragment key="placeholder">{Placeholder}</React.Fragment>,
    );

    return items;
  };

  return (
    <div className="bg-neutral-50 rounded-lg flex flex-col h-full min-h-0">
      {/* Header section - fixed */}
      <div className="p-6 pb-4 shrink-0">
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">
          CRM fields
        </h3>
        <Input
          type="text"
          placeholder="Search fields"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          icon={<MagnifyingGlass size={20} />}
          iconPosition="left"
        />
      </div>

      {/* Scrollable fields section */}
      <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-6">
        <SortableContext
          id="source"
          items={filteredCrmFields.map((f) => f.value)}
          strategy={verticalListSortingStrategy}
        >
          <div ref={setNodeRef} className="flex flex-col gap-3 min-h-[150px]">
            {renderFieldsWithPlaceholder()}
            {filteredCrmFields.length === 0 && !showPlaceholder && (
              <div className="text-center py-8 text-sm text-neutral-400 italic">
                No fields found
              </div>
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};
